/**
 * useOfflineQueue — bridges the page to the service-worker outbox.
 *
 * Public API:
 *   const { online, enqueue, drained } = useOfflineQueue();
 *
 *   • online:   navigator.onLine state (kept in sync via window events)
 *   • enqueue:  async (url, init) => "queued" | "online" | "unsupported"
 *               When offline, posts a structured message to the SW which
 *               persists the request in IndexedDB and tries to register
 *               a Background Sync. When online, returns "online" so the
 *               caller can fall through to a normal `fetch`.
 *   • drained:  count of mutations the SW has flushed since page load —
 *               components can subscribe to bump a "X requests sent"
 *               toast.
 *
 * Why a hook + SW message-channel and not a fetch interceptor?
 *   • Service workers can't intercept POST bodies that are FormData
 *     streams (we'd lose the body on first network failure).
 *   • Putting the enqueue decision in the page lets us show a
 *     friendly "Saved offline — will send when reconnected" toast
 *     instead of a generic network error.
 *   • Keeps the SW logic small and testable.
 */
import { useCallback, useEffect, useRef, useState } from "react";

// iter150AZ.67 — heartbeat helper. We POST the current depth + online
// state to the server every time the SW broadcasts a new depth (and
// the device is online). Admins see this as "Live device queues" on
// the Pending Drafts page so they know which techs have unsynced work
// on their phones. Throttled to once per 5 s per page to keep the
// chatter sane.
let _lastHeartbeatAt = 0;
let _lastHeartbeatDepth = -1;
function _sendHeartbeat(depth, online) {
  if (typeof window === "undefined") return;
  if (!online) return;
  const now = Date.now();
  // Skip if we just sent the same depth — avoids storms when the SW
  // re-broadcasts after every flush attempt.
  if (depth === _lastHeartbeatDepth && (now - _lastHeartbeatAt) < 5000) return;
  _lastHeartbeatAt = now;
  _lastHeartbeatDepth = depth;
  try {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const email = (() => {
      try {
        const raw = localStorage.getItem("usair_user");
        return raw ? (JSON.parse(raw).email || "") : "";
      } catch (_) { return ""; }
    })();
    if (!email) return;       // guests never heartbeat
    fetch(`${base}/api/me/offline-queue-heartbeat`, {
      method: "POST",
      headers: { "Content-Type": "application/json",
                  "X-User-Email": email },
      credentials: "include",
      keepalive: true,           // survive page unload
      body: JSON.stringify({
        depth, online,
        device_label: (navigator?.userAgent || "").slice(0, 80),
      }),
    }).catch(() => { /* nonfatal — admins just won't see this tick */ });
  } catch (_) { /* ignore */ }
}

export default function useOfflineQueue() {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine !== false : true
  );
  const [drained, setDrained] = useState(0);
  const [failed, setFailed] = useState(0);
  // iter150AZ.66 — live queue depth so the header pill can render
  // "📥 N drafts pending" anywhere the hook is mounted.
  const [depth, setDepth] = useState(0);

  useEffect(() => {
    const onUp = () => {
      setOnline(true);
      // Hint the SW to flush as soon as we come back. Background Sync
      // will also fire, but the explicit hint covers browsers that
      // don't support it (Safari, Firefox).
      navigator.serviceWorker?.controller?.postMessage({ type: "FLUSH_OUTBOX" });
    };
    const onDown = () => setOnline(false);
    window.addEventListener("online", onUp);
    window.addEventListener("offline", onDown);
    // Listen to SW broadcasts so any open tab can update its UI.
    const onMsg = (e) => {
      if (e.data?.type === "OUTBOX_DRAINED") setDrained((n) => n + 1);
      if (e.data?.type === "OUTBOX_FAILED")  setFailed((n) => n + 1);
      if (e.data?.type === "OUTBOX_DEPTH"  && typeof e.data.remaining === "number") {
        setDepth(e.data.remaining);
        // iter150AZ.67 — fire a heartbeat so admins see the new depth.
        _sendHeartbeat(e.data.remaining, navigator.onLine !== false);
      }
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", onMsg);
      // Ask the SW for the current depth on mount so the pill renders
      // accurately even when the page was just opened.
      if (navigator.serviceWorker.controller) {
        const ch = new MessageChannel();
        ch.port1.onmessage = (e) => {
          if (typeof e.data?.remaining === "number") {
            setDepth(e.data.remaining);
            // iter150AZ.67 — initial heartbeat on mount so admins
            // see the queue state immediately (without waiting for
            // the next enqueue/flush event).
            _sendHeartbeat(e.data.remaining, navigator.onLine !== false);
          }
        };
        try {
          navigator.serviceWorker.controller.postMessage(
            { type: "OUTBOX_QUERY_DEPTH" }, [ch.port2]);
        } catch (_) { /* SW not ready yet — listener above will catch updates */ }
      }
    }
    return () => {
      window.removeEventListener("online", onUp);
      window.removeEventListener("offline", onDown);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("message", onMsg);
      }
    };
  }, []);

  /**
   * enqueue(url, init) — call this BEFORE attempting a network request
   * if you want the offline outbox semantics. When offline, returns
   * "queued"; the caller should NOT call fetch and should show the
   * "saved for later" toast. When online, returns "online" so the
   * caller falls through to a normal fetch.
   *
   * iter150AZ.66 — FormData bodies (photos/videos from the Commissioning,
   * PM-service, and Warranty wizards) are now supported. The hook
   * extracts each part into a structured-clone-safe descriptor + Blob
   * reference, and the SW reconstructs the FormData when flushing.
   */
  const enqueue = useCallback(async (url, init = {}) => {
    if (online) return "online";
    if (!navigator.serviceWorker?.controller) return "unsupported";
    // Serialize headers — Headers / Map need to become a plain object
    // for structured-clone over the SW message channel.
    const hdrs = {};
    if (init.headers) {
      if (init.headers.forEach) {
        init.headers.forEach((v, k) => { hdrs[k] = v; });
      } else {
        Object.assign(hdrs, init.headers);
      }
    }

    // Branch: multipart vs JSON.
    let bodyOut = null;
    let partsOut = null;
    const b = init.body;
    if (b && typeof b !== "string") {
      // FormData → enumerate parts. The browser's FormData API exposes
      // .entries() so we can iterate [name, value] pairs; if value is
      // a File/Blob, it goes to the parts/blob channel; otherwise it
      // rides along as a "field" descriptor.
      if (typeof FormData !== "undefined" && b instanceof FormData) {
        partsOut = [];
        for (const [name, value] of b.entries()) {
          if (value instanceof Blob) {
            partsOut.push({
              kind: "file",
              name,
              filename: value.name || "blob",
              type: value.type || "application/octet-stream",
              value,  // raw Blob → SW will persist it to mediaBlobs
            });
          } else {
            partsOut.push({ kind: "field", name, value: String(value) });
          }
        }
        // Multipart Content-Type carries a boundary that won't survive
        // replay — strip it so the browser mints a fresh one at flush.
        delete hdrs["Content-Type"];
        delete hdrs["content-type"];
      } else if (b instanceof Blob) {
        // Raw Blob body (rare). Wrap as a single file part.
        partsOut = [{
          kind: "file", name: "file",
          filename: b.name || "blob",
          type: b.type || "application/octet-stream",
          value: b,
        }];
      } else {
        try { bodyOut = JSON.stringify(b); } catch (_) { return "unsupported"; }
      }
    } else {
      bodyOut = b || null;
    }

    const channel = new MessageChannel();
    return new Promise((resolve) => {
      const t = setTimeout(() => resolve("queued"), 1500);  // hard fallback
      channel.port1.onmessage = (e) => {
        clearTimeout(t);
        resolve(e.data?.ok ? "queued" : "unsupported");
      };
      navigator.serviceWorker.controller.postMessage({
        type: "OUTBOX_ENQUEUE",
        url, method: init.method || "POST",
        headers: hdrs,
        body:  bodyOut,
        parts: partsOut,
      }, [channel.port2]);
    });
  }, [online]);

  return { online, enqueue, drained, failed, depth };
}
